/**
 * Вход в мини-апп по подписанному initData.
 *
 * Роль приходит с сервера и определяет, какой интерфейс монтируется.
 * Это удобство, а не защита: каждый эндпоинт проверяет права сам.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { CurrentUser } from '@arbuz/shared';
import { ApiError, api } from './api';
import { initData, isTelegram } from './tg';

type AuthState =
  | { status: 'loading' }
  | { status: 'ready'; user: CurrentUser }
  | { status: 'no-telegram' }
  | { status: 'error'; message: string };

const AuthContext = createContext<{
  state: AuthState;
  refresh: () => Promise<void>;
}>({ state: { status: 'loading' }, refresh: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  async function authenticate(): Promise<void> {
    // В обычном браузере подписанных данных нет — авторизоваться нечем.
    if (!isTelegram || !initData) {
      setState({ status: 'no-telegram' });
      return;
    }

    try {
      const { user } = await api.post<{ user: CurrentUser }>('/api/auth/telegram', { initData });
      setState({ status: 'ready', user });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Не удалось связаться с сервисом';
      setState({ status: 'error', message });
    }
  }

  useEffect(() => {
    void authenticate();
  }, []);

  return (
    <AuthContext.Provider value={{ state, refresh: authenticate }}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
